import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import Decimal from "decimal.js";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "../api/trading";
import { Customer, Trader, TraderCustomerBulk } from "../api/reports";
import { parseFromFile } from "../file";
import { TicketDetailsRow } from "./models";
import { ValidationError } from "../responseResults";
import { findCustomerFrom, findTraderForCustomer } from "../customer";
import { PotentialCommodityMappings, Commodities, CommodityOptions as BaseCommodityOptions } from "../api/commodity";
import { CellFormat } from "../excel";
import { safeParseDecimal } from "../api/common";
import { ExcelComposer, SectionConfig } from "../excel/composer";

export interface ErrorsData {
  missing_customers: TicketDetailsRow[];
  failed_to_find_customers: Record<string, TicketDetailsRow[]>;
  failed_to_find_commodity: Record<string, TicketDetailsRow[]>;
}


export async function processTicketDetailsFiles({ customers, reportFiles, unsettledFiles }: { reportFiles?: File[]; unsettledFiles?: File[]; customers: Customer[] }) {
  const cogsWorkbooks = await Promise.all(
    (reportFiles || []).map(parseFromFile)
  );

  const unsettledWorkbooks = await Promise.all(
    (unsettledFiles || []).map(parseFromFile)
  );

  return processCogsAndUnsettledReport({ cogsWorkbooks, unsettledWorkbooks, customers });
}

async function processCogsAndUnsettledReport({ cogsWorkbooks, unsettledWorkbooks, customers }: { cogsWorkbooks: XLSX.WorkBook[], unsettledWorkbooks: XLSX.WorkBook[]; customers: Customer[] }) {
  const errors: ErrorsData = { missing_customers: [], failed_to_find_customers: {}, failed_to_find_commodity: {} };
  try {
    const cogsOutputWorkbook = cogsWorkbooks?.length > 0 ? loadAndTransformData({ workbooks: cogsWorkbooks, customers, errors }) : undefined;

    const unsettledOutputWorkbook = unsettledWorkbooks?.length > 0 ? loadAndTransformData({ workbooks: unsettledWorkbooks, customers, errors }) : undefined;

    return {
      cogsOutputWorkbook: await cogsOutputWorkbook?.getBuffer(),
      unsettledOutputWorkbook: await unsettledOutputWorkbook?.getBuffer(),
      error: undefined,
      errors,
    };
  } catch (error) {
    return { cogsOutputWorkbook: undefined, unsettledOutputWorkbook: undefined, error, errors }
  }
}

function aggregateSheetData<T>(workbooks: XLSX.WorkBook[]) {
  return workbooks.reduce((acc: T[], workbook: XLSX.WorkBook) => {
    let sheetName = workbook.SheetNames[0]!;
    if (!sheetName) throw new ValidationError("Failed to find sheet", {});

    let sheet = workbook.Sheets[sheetName];

    let sheetData: T[] = XLSX.utils.sheet_to_json(sheet);

    acc.push(...sheetData.map((row: T) => {
      return Object.entries(row as any).reduce((acc: T, [key, value]) => {
        (acc as any)[key.trim()] = value;
        return acc;
      }, {} as T) as any;
    }) as any[]);
    return acc;
  }, []);
}

function loadAndTransformData({ workbooks, customers, errors }: { workbooks: XLSX.WorkBook[]; customers: Customer[]; errors: ErrorsData }) {
  let sheetData: TicketDetailsRow[] = aggregateSheetData<TicketDetailsRow>(workbooks);
  sheetData.sort((a, b) => {
    return (a["Vendor Name"] || "").localeCompare(b["Vendor Name"] || "");
  });

  let rowCustomerMap = sheetData.reduce((acc: Record<string, Record<string, Record<string, TicketDetailsRow[]>>>, row: TicketDetailsRow) => {
    const commodity: string | undefined = Object.values(Commodities).find(commodity => {
      return commodity?.toLocaleLowerCase()?.trim() === row["Commodity Id"]?.toLocaleLowerCase()?.trim();
    });

    if (!row["Vendor Name"]) {
      if (!!row && Object.keys(row).length > 2) {
        errors.missing_customers.push(row);
      }
      return acc;
    }

    const customer = findCustomerFrom(
      "Master",
      row["Vendor Name"] || "",
      customers,
    );


    if (!customer) {
      if (!errors.failed_to_find_customers[row["Vendor Name"]]) { errors.failed_to_find_customers[row["Vendor Name"]] = []; }
      errors.failed_to_find_customers[row["Vendor Name"]].push(row);

      return acc;
    }

    if (!commodity) {
      const commodityKey = row["Commodity Id"] || "Empty Commodity";
      if (!errors.failed_to_find_commodity[commodityKey]) { errors.failed_to_find_commodity[commodityKey] = []; }
      errors.failed_to_find_commodity[commodityKey].push(row);
      return acc;
    }

    const countryMatch = customer.meta_data?.country || customer?.country || "Unknown";
    const stateMatch = customer.meta_data?.state || customer?.state || "Unknown";

    if (!acc[stateMatch]) acc[stateMatch] = {};
    if (!acc[stateMatch][commodity]) acc[stateMatch][commodity] = {};
    if (!acc[stateMatch][commodity][customer.id]) acc[stateMatch][commodity][customer.id] = [];

    acc[stateMatch][commodity][customer.id].push(row);
    return acc;
  }, {} as Record<string, Record<string, Record<string, TicketDetailsRow[]>>>);

  const composer = new ExcelComposer();

  for (const stateMatch of Object.keys(rowCustomerMap)) {
    composer.createSheet(stateMatch.replaceAll(/:|\/|\*|\?\[|\]/img, ""));

    composer.addSection({
      headers: [],
      data: Object.keys(rowCustomerMap[stateMatch]).map((commodity: string) => {
        let commodityNet = new Decimal(0);
        Object.keys(rowCustomerMap[stateMatch][commodity]).forEach((customerId: string) => {
          commodityNet = rowCustomerMap[stateMatch][commodity][customerId].reduce((acc, row) => {
            if (row["Applied"]) {
              return acc.plus(safeParseDecimal({})(row["Applied"]));
            }

            return acc;
          }, commodityNet);
        })
        return [{ value: stateMatch }, { value: commodity }, { value: commodityNet.toNumber(), format: CellFormat.ACCOUNTING }];
      }),
      footerCalculations: [],
      styling: {
        columns: {
          A: { width: 10 },
          B: { width: 10 },
          C: { width: 20 }
        }
      }
    } as SectionConfig, { startRow: 1, startColumn: 1 });

  }
  return composer;
}
